import { useState, useEffect } from "react";
import { Send, Star, Trophy, Trash2, Check } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { BadgeShelf } from "./BadgeShelf";
import { LightDeck } from "./LightDeck";
import { localDate } from "@/lib/dates";

const MOODS = ["🌱 Steady", "🌼 Bright", "🌧 Heavy", "🔥 Driven", "🌙 Tired"];

export const MeTab = ({ token, reflectionPrompt, onReflectionUsed, onMilestone, streakInfo, onViewBadge, palette }) => {
  const [checkinText, setCheckinText] = useState(reflectionPrompt ? "" : "");
  const [mood, setMood] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastCheckin, setLastCheckin] = useState(null);
  const [values, setValues] = useState([]);
  const [newValue, setNewValue] = useState("");
  const [newValueType, setNewValueType] = useState("value");
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/rhythm/values`, authHeaders).then((r) => setValues(r.data.items || [])).catch((e) => console.error(e));
    axios.get(`${API}/rhythm/checkins`, authHeaders).then((r) => setLastCheckin((r.data.items || [])[0] || null)).catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const submitCheckin = async () => {
    if (!checkinText.trim() && !mood) {
      toast.error("Add a few words or pick a mood");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/rhythm/checkin`, { text: checkinText, mood, client_date: localDate() }, authHeaders);
      setLastCheckin(res.data);
      setCheckinText("");
      setMood(null);
      if (reflectionPrompt) onReflectionUsed();
      toast.success(res.data.garden?.points_earned ? `Checked in — your garden grew +${res.data.garden.points_earned} 🌱` : "Checked in with yourself 💚");
      if (res.data.milestone) onMilestone(res.data.milestone);
    } catch (e) {
      toast.error("Couldn't save check-in");
    } finally {
      setSaving(false);
    }
  };

  const addValue = async () => {
    if (!newValue.trim()) return;
    try {
      const res = await axios.post(`${API}/rhythm/values`, { type: newValueType, text: newValue, client_date: localDate() }, authHeaders);
      setValues((prev) => [res.data, ...prev]);
      setNewValue("");
      toast.success(res.data.garden?.points_earned ? `Win recorded — garden grew +${res.data.garden.points_earned} 🏆🌱` : newValueType === "win" ? "Win recorded 🏆" : "Value saved ⭐");
      if (res.data.milestone) onMilestone(res.data.milestone);
    } catch (e) {
      toast.error("Couldn't save");
    }
  };

  const removeValue = async (id) => {
    try {
      await axios.delete(`${API}/rhythm/values/${id}`, authHeaders);
      setValues((prev) => prev.filter((v) => v.id !== id));
    } catch (e) {
      toast.error("Couldn't remove");
    }
  };

  return (
    <div className="space-y-5" data-testid="rhythm-me-tab">
      <BadgeShelf streakInfo={streakInfo} onViewBadge={onViewBadge} palette={palette} />

      {/* Be The Light card deck */}
      <LightDeck token={token} onMilestone={onMilestone} palette={palette} />

      {/* Daily check-in */}
      <div className="rounded-2xl border bg-white p-5" style={{ borderColor: reflectionPrompt ? palette.primary : palette.border, borderWidth: reflectionPrompt ? 2 : 1 }} data-testid="daily-checkin-card">
        <h2 className="text-lg font-bold font-body mb-1" style={{ color: palette.primary }}>Daily Check-in</h2>
        {reflectionPrompt ? (
          <p className="text-xs mb-3 rounded-xl p-3" style={{ backgroundColor: `${palette.primary}12`, color: palette.primary }} data-testid="reflection-prompt">
            🌙 {reflectionPrompt}
          </p>
        ) : (
          <p className="text-xs opacity-70 mb-3">A few honest words, just for you</p>
        )}
        <div className="flex flex-wrap gap-2 mb-3">
          {MOODS.map((m) => (
            <button
              key={m}
              onClick={() => setMood(mood === m ? null : m)}
              className="text-xs px-3 py-1.5 rounded-full border transition-colors"
              style={{
                borderColor: mood === m ? palette.primary : palette.border,
                backgroundColor: mood === m ? `${palette.primary}15` : "#FFFFFF",
                color: mood === m ? palette.primary : palette.text,
              }}
              data-testid={`mood-chip-${m.slice(3)}`}
            >
              {m}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <textarea
            rows={2}
            className="flex-1 rounded-xl border px-3 py-2 text-sm outline-none resize-none"
            style={{ borderColor: palette.border }}
            placeholder="How are you, really?"
            value={checkinText}
            onChange={(e) => setCheckinText(e.target.value)}
            data-testid="checkin-input"
          />
          <button
            onClick={submitCheckin}
            disabled={saving}
            className="rounded-xl px-4 text-white self-stretch"
            style={{ backgroundColor: palette.primary, opacity: saving ? 0.6 : 1 }}
            data-testid="checkin-submit-btn"
            aria-label="Save check-in"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {lastCheckin && (
          <p className="text-[11px] mt-2 flex items-center gap-1" style={{ color: palette.primary }}>
            <Check className="w-3 h-3" /> Last check-in: {lastCheckin.mood ? `${lastCheckin.mood} · ` : ""}{lastCheckin.text?.slice(0, 60) || "logged"}
          </p>
        )}
      </div>

      {/* Values & wins */}
      <div className="rounded-2xl border bg-white p-5" style={{ borderColor: palette.border }} data-testid="values-wins-card">
        <h2 className="text-lg font-bold font-body mb-3" style={{ color: palette.primary }}>Values & Wins</h2>
        <div className="flex gap-2 mb-4">
          <select
            className="rounded-xl border px-2 py-2 text-xs bg-white"
            style={{ borderColor: palette.border }}
            value={newValueType}
            onChange={(e) => setNewValueType(e.target.value)}
            data-testid="value-type-select"
          >
            <option value="value">⭐ Value</option>
            <option value="win">🏆 Win</option>
          </select>
          <input
            className="flex-1 rounded-xl border px-3 py-2 text-sm outline-none"
            style={{ borderColor: palette.border }}
            placeholder={newValueType === "win" ? "A win worth remembering..." : "A value you live by..."}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addValue()}
            data-testid="value-input"
          />
          <button
            onClick={addValue}
            className="rounded-xl px-3 text-white text-sm font-bold"
            style={{ backgroundColor: palette.primary }}
            data-testid="value-add-btn"
          >
            +
          </button>
        </div>
        <div className="space-y-2">
          {values.map((v) => (
            <div key={v.id} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5" style={{ backgroundColor: `${palette.primary}0D` }} data-testid={`value-item-${v.type}`}>
              {v.type === "win" ? (
                <Trophy className="w-4 h-4 flex-shrink-0" style={{ color: "#B08A3E" }} />
              ) : (
                <Star className="w-4 h-4 flex-shrink-0" style={{ color: palette.primary }} />
              )}
              <span className="text-sm flex-1">{v.text}</span>
              <button onClick={() => removeValue(v.id)} className="p-1 rounded-full hover:bg-black/5" aria-label="Remove">
                <Trash2 className="w-3.5 h-3.5" style={{ color: "#9AA69E" }} />
              </button>
            </div>
          ))}
          {values.length === 0 && <p className="text-xs opacity-60 text-center py-2">Nothing saved yet — add your first value or win above</p>}
        </div>
      </div>
    </div>
  );
};
